import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { MapPin, CheckCircle2, MessageCircle, Award } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button, Avatar, Stars, Tag, Loading, EmptyState } from "@/components/brand";
import { PageHeader } from "@/components/PageHeader";
import { ClassCard, ChallengeCard } from "@/components/cards";
import { formatR } from "@/lib/format";

export default function TrainerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useQuery({ queryKey: ["trainer", id], queryFn: () => api(`/trainers/${id}`) });

  const message = useMutation({
    mutationFn: () => api("/conversations", { method: "POST", body: { trainer_id: id } }),
    onSuccess: (c) => navigate(`/chat/${c.id}`),
    onError: (e) => toast.error(e.message),
  });

  if (t.isLoading) return <Loading />;
  if (!t.data) return <><PageHeader /><EmptyState title="Trainer not found" /></>;
  const d = t.data;

  return (
    <div data-testid="trainer-detail-screen">
      <PageHeader onBack={() => navigate(-1)} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="relative h-72 rounded-xl2 overflow-hidden">
            <img src={d.avatar || d.cover} alt={d.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
            <div className="absolute left-4 right-4 bottom-4">
              <div className="flex items-center gap-1.5">
                <span className="font-display text-2xl text-white">{d.name}</span>
                {d.verified ? <CheckCircle2 size={18} className="text-brand" /> : null}
              </div>
              <div className="font-body text-sm text-white/85">{d.headline}</div>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3">
            <Button testID="trainer-book-button" title="Book Private Session" onClick={() => navigate(`/book-private/${d.id}`)} disabled={user?.role === "trainer"} full />
            {user?.role === "member" ? <Button testID="trainer-message-button" title="Message Trainer" icon={MessageCircle} variant="outline" loading={message.isPending} onClick={() => message.mutate()} full /> : null}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="flex flex-wrap items-center gap-4">
            <Stars rating={d.rating} count={d.review_count} size={16} />
            <span className="font-body text-sm text-mutedgrey">{d.sessions_completed} sessions · {d.experience_years}y experience</span>
            {d.location ? <span className="inline-flex items-center gap-1 font-body text-sm text-mutedgrey"><MapPin size={14} />{d.location}</span> : null}
          </div>
          <div className="flex flex-wrap gap-2">{(d.specialties || []).map((s) => <Tag key={s} label={s} />)}</div>
          {d.bio ? <p className="font-body text-mutedgrey leading-relaxed">{d.bio}</p> : null}

          <div>
            <h3 className="font-display text-xl text-ink mb-2">Private Session Pricing</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {(d.private_pricing || []).map((p) => (
                <button key={p.duration_min} data-testid={`trainer-price-${p.duration_min}`} onClick={() => navigate(`/book-private/${d.id}?duration=${p.duration_min}`)}
                  className="p-4 rounded-xl2 border border-line hover:border-brand text-center transition-colors">
                  <div className="font-body font-semibold text-sm text-ink">{p.duration_min} min</div>
                  <div className="font-display text-2xl text-brand">{formatR(p.price)}</div>
                </button>
              ))}
            </div>
          </div>

          {(d.challenges || []).length > 0 ? (
            <div>
              <h3 className="font-display text-xl text-ink mb-2 flex items-center gap-2"><Award size={18} className="text-brand" />Trainer Challenges</h3>
              <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">{d.challenges.map((c) => <ChallengeCard key={c.id} item={c} compact />)}</div>
            </div>
          ) : null}

          {(d.upcoming_classes || []).length > 0 ? (
            <div>
              <h3 className="font-display text-xl text-ink mb-2">Upcoming Classes</h3>
              <div className="grid sm:grid-cols-2 gap-4">{d.upcoming_classes.map((c) => <ClassCard key={c.id} item={c} />)}</div>
            </div>
          ) : null}

          {(d.reviews || []).length > 0 ? (
            <div>
              <h3 className="font-display text-xl text-ink mb-2">Reviews</h3>
              <div className="space-y-3">
                {d.reviews.map((r) => (
                  <div key={r.id} className="p-4 rounded-xl2 border border-divider">
                    <div className="flex items-center gap-2"><Avatar uri={r.user_avatar} size={32} name={r.user_name} /><span className="font-body font-semibold text-sm text-ink">{r.user_name}</span><div className="ml-auto"><Stars rating={r.rating} size={12} /></div></div>
                    {r.comment ? <p className="font-body text-sm text-mutedgrey mt-2">{r.comment}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
